import {
    useContext,
    type FC
} from "react";
import {
    type TextInputKeyPressEventData,
    type NativeSyntheticEvent,
    Platform,
    View
} from "react-native";
import type IQuestionnaireInputProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    QuestionnaireItemPartContentProps
} from "../../type";
import {
    getQuestionnaireIsPlainEnterEvent,
    getQuestionnaireInputText
} from "../../util";
import {
    withQuestionnaireItemContext
} from "../../context";
import {
    CardContext
} from "../../../card/context";
import TextAreaInput from "../../../textAreaInput";
import TextInput from "../../../textInput";

const QuestionnaireInputContent: FC<QuestionnaireItemPartContentProps<IQuestionnaireInputProps>> = ({
    customTheme: customThemeProp,
    itemContext,
    context,
    style,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const cardContext = useContext(CardContext);

    const {
        shapeConfigs,
        typography,
        radiuses,
        borders,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        control: controlDynamicStyle
    } = useStyles({
        inset: cardContext?.inset ?? spaces.spacingMd,
        shapeConfigs,
        radiuses,
        borders
    });

    const {
        item
    } = itemContext;

    const input = item.input;

    if(!input) {
        return null;
    }

    const handleKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if(!getQuestionnaireIsPlainEnterEvent(event)) {
            return;
        }

        event.preventDefault();

        context.handleNext();
    };

    const submitProps = Platform.OS === "web" ? {
        onKeyPress: handleKeyPress
    } : {
        onSubmitEditing: context.handleNext,
        blurOnSubmit: false
    };

    const inputProps = {
        ...submitProps,
        type: itemContext.isInvalid && !item.choices?.length ? "danger" as const : "neutral" as const,
        isDisabled: context.isDisabled || itemContext.isLocked,
        contentContainerStyle: controlDynamicStyle,
        customLocalize: context.customLocalize,
        spreadBehaviour: "stretch" as const,
        value: getQuestionnaireInputText({
            answer: itemContext.answer,
            item
        }),
        onChangeText: (text: string) => {
            context.handleInputChange({
                item,
                text
            });
        },
        placeholder: input.placeholder,
        maxLength: input.maxLength,
        "aria-label": input.title,
        hintText: input.hintText,
        size: context.size,
        customTheme
    };

    if(input.isMultiline) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.container
            ]}
        >
            <TextAreaInput
                {...inputProps}
                minHeight={typography.bodyMediumSize.lineHeight * 3 + spaces.spacingSm * 2}
                ref={context.registerInput}
                isUpdateOnRealtime={true}
            />
        </View>;
    }

    return <View
        {...props}
        style={[
            style,
            stylesheet.container
        ]}
    >
        <TextInput
            {...inputProps}
            ref={context.registerInput}
        />
    </View>;
};
export default withQuestionnaireItemContext("Input", QuestionnaireInputContent);
